import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Plus, LogIn, Loader2, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import postService, { DartPost } from '../../services/postService';
import PostCard from './PostCard';
import PostComposer from './PostComposer';
import { t } from '../../i18n';

const ACCENT = '#3fced1';
const PAGE_SIZE = 10;

export type PostFeedMode = 'feed' | 'forYou';

interface PostFeedListProps {
  /** 'feed' = alle neuesten Beiträge, 'forYou' = gefolgte Autoren (auth). */
  mode: PostFeedMode;
}

/**
 * Wiederverwendbare Feed-Liste (Startseiten-Tabs + /feed): lädt den
 * öffentlichen Feed bzw. "Für dich" (ohne Login → Anmelde-Hinweis statt
 * API-Call), zeigt PostCards max. 600px zentriert mit "Mehr laden"-Pagination
 * und einen Posten-Button, der den PostComposer öffnet (Gäste → Login-Redirect
 * mit Rücksprung, Muster ListingFollowButton).
 */
const PostFeedList: React.FC<PostFeedListProps> = ({ mode }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [posts, setPosts] = useState<DartPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);

  const fetchPage = useCallback(
    async (offset: number) => {
      const params = { limit: PAGE_SIZE, offset };
      return mode === 'forYou' ? postService.getForYou(params) : postService.getFeed(params);
    },
    [mode]
  );

  useEffect(() => {
    let cancelled = false;
    if (mode === 'forYou' && !isAuthenticated) {
      setPosts([]);
      setLoading(false);
      setHasMore(false);
      return;
    }
    setLoading(true);
    fetchPage(0).then((result) => {
      if (cancelled) return;
      setPosts(result.posts);
      setHasMore(result.posts.length >= PAGE_SIZE);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [mode, isAuthenticated, fetchPage]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    const result = await fetchPage(posts.length);
    setPosts((prev) => {
      const known = new Set(prev.map((p) => p.id));
      return [...prev, ...result.posts.filter((p) => !known.has(p.id))];
    });
    setHasMore(result.posts.length >= PAGE_SIZE);
    setLoadingMore(false);
  };

  const handleRemoved = (postId: string) => {
    setPosts((prev) => prev.filter((p) => p.id !== postId));
  };

  const handleCreated = (post: DartPost) => {
    setPosts((prev) => [post, ...prev]);
  };

  // Gäste dürfen den Composer nicht nutzen → Login mit Rücksprung
  const openComposer = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: location.pathname } } });
      return;
    }
    setComposerOpen(true);
  };

  // ?neu=1 (z.B. aus dem "Neuer Eintrag"-Modal) öffnet den Composer direkt —
  // Param danach aus der URL entfernen, damit Reload ihn nicht erneut öffnet
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('neu') === '1') {
      navigate(location.pathname, { replace: true });
      openComposer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const loginHint = mode === 'forYou' && !isAuthenticated;

  return (
    <div className="w-full max-w-[600px] mx-auto">
      {/* Posten-Button (Gäste → Login-Redirect) */}
      <div className="flex justify-end mb-5">
        <button
          type="button"
          onClick={openComposer}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold text-black transition hover:opacity-90 shrink-0"
          style={{ backgroundColor: ACCENT }}
        >
          <Plus size={16} />
          {t('web.component.post-composer.posten-2')}
        </button>
      </div>

      {loginHint ? (
        <div className="text-center py-16">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center">
            <LogIn size={20} style={{ color: ACCENT }} />
          </div>
          <p className="text-zinc-400 mb-4">{t('web.component.post-feed-list.melde-dich-an-um-deinen')}</p>
          <Link
            to="/login"
            className="inline-block px-5 py-2.5 rounded-xl text-sm font-semibold text-black transition hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            {t('web.component.header.anmelden')}
          </Link>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={28} className="animate-spin text-zinc-500" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center">
            <ImageIcon size={20} style={{ color: ACCENT }} />
          </div>
          <p className="text-zinc-400">
            {mode === 'forYou'
              ? t('web.component.post-feed-list.noch-keine-beitraege-folge-spielern')
              : t('web.component.post-feed-list.noch-keine-beitraege-sei-der')}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-10">
            {posts.map((post) => (
              <PostCard key={post.id} post={post} onRemoved={handleRemoved} />
            ))}
          </div>

          {hasMore && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="px-5 py-2.5 rounded-xl border border-zinc-700 text-sm text-zinc-300 hover:bg-zinc-800 transition disabled:opacity-60"
              >
                {loadingMore ? t('web.component.listing-explorer.wird-geladen') : t('web.component.comment-section.mehr-laden')}
              </button>
            </div>
          )}
        </>
      )}

      <PostComposer
        isOpen={composerOpen}
        onClose={() => setComposerOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
};

export default PostFeedList;
